import React, { useEffect, useState, useRef } from "react";
import "./ResourceActionsSidebar.css";
import { useSelector, useDispatch } from "react-redux";
import {
  selectLinkRecords,
  selectUIStates,
  setUIStates,
} from "../../redux/features/appData/appDataSlice";
import { LinkRecordAction, LinkRecordActionType } from "winslow-core";
import {
  apiGetCollectionLinkRecordActions,
  apiGetLinkRecordActions,
} from "../../services/CollectionService";
import { selectSupportedApps } from "../../redux/features/connectedApps/connectedAppsSlice";
import { selectUser } from "../../redux/features/appData/appDataSlice";
import { ResourceActionMulti } from "./ResourceActionMulti";
import { ResourceActionSingle } from "./ResourceActionSingle";

interface ResourceActionsSidebarProps {
  collectionId: string;
  linkRecordId: string | null;
}

export const ResourceActionsSidebar: React.FC<ResourceActionsSidebarProps> = ({
  collectionId,
  linkRecordId,
}) => {
  const [linkRecordActions, setLinkRecordActions] = useState<
    LinkRecordAction[]
  >([]);
  const actionMessagesRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);
  const linkRecords = useSelector(selectLinkRecords) || undefined;
  const supportedApps = useSelector(selectSupportedApps);
  const storeUser = useSelector(selectUser);

  const linkRecord = linkRecords?.find(
    (linkRecord) => linkRecord.id === linkRecordId
  );

  const formatActionType = (actionType: LinkRecordActionType): string => {
    const typeMap: Record<LinkRecordActionType, string> = {
      [LinkRecordActionType.CREATED]: "Created",
      [LinkRecordActionType.UPDATED]: "Updated",
      [LinkRecordActionType.DELETED]: "Deleted",
      [LinkRecordActionType.CONNECTED]: "Connected",
      [LinkRecordActionType.EMBEDDED]: "Synced",
      [LinkRecordActionType.REMOVED]: "Removed",
      [LinkRecordActionType.ERRORED]: "Errored",
    };
    return typeMap[actionType] || actionType;
  };

  const onClose = () => {
    dispatch(setUIStates({ ...uiStates, resourceSidebarOpen: false }));
  };

  useEffect(() => {
    if (!uiStates.resourceSidebarOpen) {
      setLinkRecordActions([]);
      return;
    }

    if (uiStates.resourceSidebarOpen) {
      (async () => {
        if (linkRecordId) {
          const actions = await apiGetLinkRecordActions(linkRecordId);

          setLinkRecordActions(
            actions?.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            ) || []
          );
        } else {
          const actions = await apiGetCollectionLinkRecordActions(collectionId);
          setLinkRecordActions(
            actions?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            ) || []
          );
        }
      })();
    }
  }, [uiStates.resourceSidebarOpen, linkRecordId, collectionId]);

  useEffect(() => {
    if (actionMessagesRef.current) {
      actionMessagesRef.current.scrollTop =
        actionMessagesRef.current.scrollHeight;
    }
  }, [linkRecordActions]);

  useEffect(() => {
    return () => {
      setLinkRecordActions([]);
    };
  }, []);

  if (!uiStates.resourceSidebarOpen) return null;

  return (
    <div className="resource-sidebar">
      <div className="resource-sidebar-body">
        <div className="resource-info">
          <div className="row resource-sidebar-header">
            <div className="resource-header">
              {!linkRecordId && (
                <div className="author-info">All Resources</div>
              )}
            </div>
            <div className="resource-sidebar-close" onClick={onClose}>
              <img
                className="close-icon"
                src="/icons/close-icon.svg"
                alt="Close"
              />
            </div>
          </div>

          <div ref={actionMessagesRef}>
            {linkRecordId ? (
              <ResourceActionSingle
                linkRecordActions={linkRecordActions}
                linkRecord={linkRecord}
                supportedApps={supportedApps}
                storeUser={storeUser}
                formatActionType={formatActionType}
              />
            ) : (
              <ResourceActionMulti
                linkRecordActions={linkRecordActions}
                linkRecords={linkRecords}
                supportedApps={supportedApps}
                storeUser={storeUser}
                formatActionType={formatActionType}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
